/**
 * PersistenceObservability (AJIP-16.02.004 · WS12)
 * ---------------------------------------------------------------------
 * 12 canonical persistence event kinds. Layered on top of the Core
 * Engine's Observability class from AJIP-16.02.003. No dashboard, no
 * customer tracking, no profiling — this is internal instrumentation
 * only.
 */

const PERSISTENCE_EVENTS = Object.freeze({
  PERSISTENCE_SAVE_REQUESTED: 'PERSISTENCE_SAVE_REQUESTED',
  PERSISTENCE_SAVE_COMPLETED: 'PERSISTENCE_SAVE_COMPLETED',
  PERSISTENCE_SAVE_FAILED: 'PERSISTENCE_SAVE_FAILED',
  PERSISTENCE_LOAD_REQUESTED: 'PERSISTENCE_LOAD_REQUESTED',
  PERSISTENCE_LOAD_COMPLETED: 'PERSISTENCE_LOAD_COMPLETED',
  PERSISTENCE_LOAD_FAILED: 'PERSISTENCE_LOAD_FAILED',
  PERSISTENCE_RESTORE_COMPLETED: 'PERSISTENCE_RESTORE_COMPLETED',
  PERSISTENCE_RESTORE_FAILED: 'PERSISTENCE_RESTORE_FAILED',
  PERSISTENCE_RECORD_EXPIRED: 'PERSISTENCE_RECORD_EXPIRED',
  PERSISTENCE_MIGRATION_COMPLETED: 'PERSISTENCE_MIGRATION_COMPLETED',
  PERSISTENCE_MIGRATION_FAILED: 'PERSISTENCE_MIGRATION_FAILED',
  PERSISTENCE_CONFLICT_DETECTED: 'PERSISTENCE_CONFLICT_DETECTED',
});

const ALL_PERSISTENCE_EVENTS = Object.freeze(Object.values(PERSISTENCE_EVENTS));

class PersistenceObservability {
  constructor() {
    this._handlers = new Map();
    for (const k of ALL_PERSISTENCE_EVENTS) this._handlers.set(k, new Set());
    this._enabled = true;
  }

  on(eventKind, handler) {
    if (!ALL_PERSISTENCE_EVENTS.includes(eventKind)) return () => {};
    if (typeof handler !== 'function') return () => {};
    this._handlers.get(eventKind).add(handler);
    return () => this._handlers.get(eventKind).delete(handler);
  }

  emit(eventKind, payload) {
    if (!this._enabled) return 0;
    if (!ALL_PERSISTENCE_EVENTS.includes(eventKind)) return 0;
    const handlers = this._handlers.get(eventKind);
    let count = 0;
    for (const h of handlers) {
      try {
        h(Object.freeze({ event: eventKind, at: Date.now(), payload }));
        count += 1;
      } catch (_e) {
        // Isolate handler failures. See AJIP-16.02.003 Observability.
      }
    }
    return count;
  }

  disable() { this._enabled = false; }
  enable() { this._enabled = true; }
  isEnabled() { return this._enabled; }
  listenerCount(eventKind) {
    if (!ALL_PERSISTENCE_EVENTS.includes(eventKind)) return 0;
    return this._handlers.get(eventKind).size;
  }
  clear() {
    for (const set of this._handlers.values()) set.clear();
  }
}

export { PERSISTENCE_EVENTS, ALL_PERSISTENCE_EVENTS, PersistenceObservability };
